import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { trackEvent } from "helpers/analyticsHelpers";
import { useSwitcherClient } from "./useSwitcherClient";
import { useNavigate } from "react-router-dom";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import { closeCurrentModal, setActiveModal } from "store/modal/slice";
import { Modals } from "store/modal/types";
import { AttentionModal } from "components/modal/AttentionModal";

export interface CancelSubscriptionOptions {
    onCancel?: () => void | Promise<void>;
    onBeforeConfirm?: () => void | Promise<void>;
    overrideCancel?: () => void | Promise<void>;
}

export const useCancelSubscription = (
    subscriptionId: string,
    customerId: string,
    { overrideCancel, onBeforeConfirm, onCancel }: CancelSubscriptionOptions
) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { dispatchApiRequest: deleteSubscription, loading: cancelLoading } =
        useSwitcherClient((client) => client.stripe_DeleteSubscription, {
            requestImmediately: false
        });

    const cancelSub = useCallback(async () => {
        try {
            if (overrideCancel) {
                await overrideCancel();
            } else {
                await deleteSubscription([subscriptionId]);
            }
            await onCancel?.();
            navigate("/subscription");
        } catch (e) {
            dispatch(
                addNotification({
                    type: NotificationType.Danger,
                    message: e?.message
                })
            );
        }
    }, [
        overrideCancel,
        onCancel,
        navigate,
        deleteSubscription,
        subscriptionId,
        dispatch
    ]);

    const closeAttentionModal = useCallback(() => {
        dispatch(closeCurrentModal());
    }, [dispatch]);

    const openAttentionModal = useCallback(
        () =>
            dispatch(
                setActiveModal({
                    id: Modals.AttentionModal,
                    type: Modals.AttentionModal,
                    component: (
                        <AttentionModal
                            isOpen={true}
                            setIsOpen={closeAttentionModal}
                            handleCancel={closeAttentionModal}
                            hasContinueButton={true}
                            handleContinue={cancelSub}
                            continueText={t("buttons:confirm")}
                        >
                            <div>{t("messages:cancel-confirmation")}</div>
                        </AttentionModal>
                    )
                })
            ),
        [closeAttentionModal, dispatch, t, cancelSub]
    );

    const initiateProfitwellRetainAttempt = useCallback(
        async (subscriptionId: string) => {
            try {
                onBeforeConfirm?.();
                const profitwellResponse = await profitwell(
                    "init_cancellation_flow",
                    { subscription_id: subscriptionId }
                );
                if (profitwellResponse) {
                    trackEvent("ProfitWell Retain Attempt", {
                        profitWellResponse: profitwellResponse,
                        subscriptionId,
                        customerId
                    });
                }

                if (!profitwellResponse) {
                    openAttentionModal();
                } else if (
                    profitwellResponse.status === "retained" ||
                    profitwellResponse.status === "aborted"
                ) {
                    return;
                } else {
                    await cancelSub();
                }
            } catch (e) {
                openAttentionModal();
            }
        },
        [cancelSub, customerId, onBeforeConfirm, openAttentionModal]
    );
    return {
        /** attempt to retain customer with coupons thru profitwell and ultimately cancel subscription */
        initiateProfitwellRetainAttempt,
        /** the attempt to cancel the subscription is loading */
        cancelLoading
    };
};
